import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { StatusCode } from "api/protocols";
import { GetTeeSheetNotificationAll } from "api/rpc/2024-04/facilityAdmin/teesheet/notification";

import { showError } from "redux/actions/ui";
import { TTeesheetNotification } from "redux/reducers/models/teesheet";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import DataTable from "components/dataTable/DataTable";
import MessageDisplay from "components/messageDisplay/MessageDisplay";

import "./teeSheetNotification.scss";

const NOTIFICATION_TABLE_COLUMN_COUNT = 6;

type TTeesheetNotificationGroup = {
  id: string;
  notificationGroupId?: string | number;
  notifications: TTeesheetNotification[];
  primaryNotification: TTeesheetNotification;
  startDate: string;
  endDate: string;
  isGrouped: boolean;
};

export default function TeeSheetNotifications() {
  const history = useHistory();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [notifications, setNotifications] = useState<TTeesheetNotification[]>(undefined);
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());
  const [filters] = useState<{ date?: Date }>({
    date: undefined,
  });

  const notificationGroups = useMemo<TTeesheetNotificationGroup[]>(() => {
    if (!notifications) {
      return [];
    }

    const groupedNotifications = notifications.reduce<Record<string, TTeesheetNotification[]>>((groups, notification) => {
      const groupKey =
        notification.notification_group_id != null
          ? `group-${String(notification.notification_group_id)}`
          : `notification-${String(notification.id)}`;

      groups[groupKey] = [...(groups[groupKey] ?? []), notification];
      return groups;
    }, {});

    return Object.entries(groupedNotifications)
      .map(([groupKey, groupNotifications]) => {
        const sortedNotifications = [...groupNotifications].sort((firstNotification, secondNotification) => {
          return moment.utc(firstNotification.start_date).valueOf() - moment.utc(secondNotification.start_date).valueOf();
        });
        const primaryNotification = sortedNotifications[0];
        const startDate = sortedNotifications[0].start_date;
        const endDate = sortedNotifications[sortedNotifications.length - 1].end_date;
        const notificationGroupId = primaryNotification.notification_group_id;
        const isGrouped = notificationGroupId != null && sortedNotifications.length > 1;

        return {
          id: groupKey,
          notificationGroupId,
          notifications: sortedNotifications,
          primaryNotification,
          startDate,
          endDate,
          isGrouped,
        };
      })
      .sort((firstGroup, secondGroup) => {
        return moment.utc(firstGroup.startDate).valueOf() - moment.utc(secondGroup.startDate).valueOf();
      });
  }, [notifications]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTeeSheetNotifications(filters.date, source.token);
    return () => source.cancel();
  }, [filters.date]);

  async function loadTeeSheetNotifications(date?: Date, token?: CancelToken) {
    if (notifications !== undefined) {
      setNotifications(undefined);
    }
    const res = await GetTeeSheetNotificationAll({ date: date }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading teesheet notifications.")));
    }

    setNotifications(res.status !== StatusCode.OK ? [] : res.data);
  }

  const primaryAction = {
    content: t("New Notification"),
    action: () => history.push("/admin/settings/tee-sheet/notification/new"),
  };

  function navigateToNotification(notificationId: string | number) {
    history.push(`/admin/settings/tee-sheet/notification/${String(notificationId)}`);
  }

  function navigateToNotificationGroup(notificationGroupId: string | number) {
    history.push(`/admin/settings/tee-sheet/notification/group/${String(notificationGroupId)}`);
  }

  function toggleNotificationGroup(notificationGroupId: string | number) {
    setExpandedGroupIds(prev => {
      const nextExpandedGroupIds = new Set(prev);
      const groupId = String(notificationGroupId);

      if (nextExpandedGroupIds.has(groupId)) {
        nextExpandedGroupIds.delete(groupId);
      } else {
        nextExpandedGroupIds.add(groupId);
      }

      return nextExpandedGroupIds;
    });
  }

  function renderNotificationRow(notification: TTeesheetNotification, options?: { isChildRow?: boolean }) {
    const isChildRow = options?.isChildRow ?? false;
    return (
      <tr
        key={notification.id}
        className={`clickable ${isChildRow ? "notification-group-child-row" : ""}`}
        onClick={() => navigateToNotification(notification.id)}
      >
        <td />
        <td>{moment.utc(notification.start_date).format("MMM DD, YYYY")}</td>
        <td>{moment.utc(notification.end_date).format("MMM DD, YYYY")}</td>
        <td>{notification.subject}</td>
        <td>{notification.user_type === "admin" ? t("Internal") : t("External")}</td>
        <td className="notification-row-edit-cell">
          <FontAwesomeIcon icon={["far", "pencil"]} />
        </td>
      </tr>
    );
  }

  return (
    <Page
      title={t("Tee Sheet Notifications")}
      subtitle={t("Create a Tee Sheet notification")}
      primaryAction={primaryAction}
    >
      <DataTable
        columns={[
          { label: "", width: "48px" },
          { label: t("Start Date") },
          { label: t("End Date") },
          { label: t("Subject") },
          { label: t("Type") },
          { label: "", width: "48px" },
        ]}
        loading={notifications === undefined}
      >
        {notifications?.length === 0 ? (
          <tr>
            <td colSpan={NOTIFICATION_TABLE_COLUMN_COUNT}>
              <MessageDisplay
                icon="info-circle"
                title={t("No notifications found")}
                content={t("Create a new notification to populate this list")}
              />
            </td>
          </tr>
        ) : null}

        {notificationGroups.map(notificationGroup => {
          if (!notificationGroup.isGrouped) {
            return renderNotificationRow(notificationGroup.primaryNotification);
          }

          const notificationGroupId = notificationGroup.notificationGroupId;

          if (notificationGroupId == null) {
            return renderNotificationRow(notificationGroup.primaryNotification);
          }

          const isExpanded = expandedGroupIds.has(String(notificationGroupId));

          return (
            <React.Fragment key={notificationGroup.id}>
              <tr
                className="clickable notification-group-parent-row"
                onClick={() => navigateToNotificationGroup(notificationGroupId)}
              >
                <td
                  className="notification-group-toggle-cell"
                  onClick={event => {
                    event.stopPropagation();
                    toggleNotificationGroup(notificationGroupId);
                  }}
                >
                  <FontAwesomeIcon icon={isExpanded ? ["far", "chevron-up"] : ["far", "chevron-down"]} />
                </td>
                <td>
                  <div className="notification-group-start-cell">
                    <span>{moment.utc(notificationGroup.startDate).format("MMM DD, YYYY")}</span>
                    <span className="notification-group-count">{notificationGroup.notifications.length}</span>
                  </div>
                </td>
                <td>{moment.utc(notificationGroup.endDate).format("MMM DD, YYYY")}</td>
                <td>{notificationGroup.primaryNotification.subject}</td>
                <td>{notificationGroup.primaryNotification.user_type === "admin" ? t("Internal") : t("External")}</td>
                <td className="notification-row-edit-cell">
                  <FontAwesomeIcon icon={["far", "pencil"]} />
                </td>
              </tr>

              {isExpanded
                ? notificationGroup.notifications.map(notification =>
                    renderNotificationRow(notification, { isChildRow: true }),
                  )
                : null}
            </React.Fragment>
          );
        })}
      </DataTable>
    </Page>
  );
}
